import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';
import { social } from './paths.js';

export interface Person {
  id: number;
  username: string;
  avatar: string | null;
}

interface Collaborator extends Person {
  addedAt: string;
  accepted: boolean;
}

interface Roster {
  owner: Person | null;
  isOwner: boolean;
  me: number | null;
  collaborators: Collaborator[];
}

/**
 * Co-op: who is in a project. The owner invites people by username; each must
 * accept before the project opens for them. Everyone in a project shares its
 * window and its Prisma.
 */
export function ShareDialog({ projectId, projectName, onClose }: { projectId: number; projectName: string; onClose: () => void }) {
  const [roster, setRoster] = useState<Roster | null>(null);
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRoster(await api.get<Roster>(social(`/projects/${projectId}/collaborators`)));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load who is in this project.');
    }
  }, [projectId]);

  useEffect(() => void load(), [load]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(social(`/projects/${projectId}/collaborators`), { username: username.trim() });
      setUsername('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the invitation.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(person: Person, label: string) {
    if (!window.confirm(label)) return;
    try {
      await api.del(social(`/projects/${projectId}/collaborators/${person.id}`));
      await load();
      if (person.id === roster?.me) onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove them.');
    }
  }

  return (
    <div className="soc-backdrop" role="dialog" aria-modal="true" aria-label={`Share ${projectName}`} onClick={onClose}>
      <div className="soc-dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Share “{projectName}”</h2>
          <button type="button" className="ghost" onClick={onClose}>
            Close
          </button>
        </header>
        <p className="muted small">Everyone in a project works in the same project window and talks to the same Prisma. People you invite must accept first.</p>
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        {roster?.isOwner && (
          <form className="soc-row" onSubmit={invite}>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Their username" maxLength={60} autoFocus />
            <button className="primary" type="submit" disabled={busy || !username.trim()}>
              {busy ? 'Inviting…' : 'Invite'}
            </button>
          </form>
        )}
        {roster === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <ul className="soc-list">
            {roster.owner && (
              <li>
                <span>
                  <strong>{roster.owner.username}</strong> <span className="soc-tag">owner</span>
                </span>
              </li>
            )}
            {roster.collaborators.map((c) => (
              <li key={c.id}>
                <span>
                  <strong>{c.username}</strong> {c.accepted ? <span className="soc-tag">in the project</span> : <span className="soc-tag soc-wait">invited, waiting</span>}
                </span>
                {roster.isOwner ? (
                  <button type="button" className="ghost danger" onClick={() => void remove(c, c.accepted ? `Remove ${c.username} from this project?` : `Take back the invitation to ${c.username}?`)}>
                    {c.accepted ? 'Remove' : 'Cancel'}
                  </button>
                ) : c.id === roster.me ? (
                  <button type="button" className="ghost danger" onClick={() => void remove(c, 'Leave this project? You will need a new invitation to come back.')}>
                    Leave
                  </button>
                ) : null}
              </li>
            ))}
            {roster.collaborators.length === 0 && <li className="muted">Nobody else yet.</li>}
          </ul>
        )}
      </div>
    </div>
  );
}
